import type { FormAnswers, FormSchema } from "@/components/mobile/activity/formSchema";
import { addDays } from "@/components/reports/reportModel";
import type { ReportPayload, ReportSubmission, ReportTemplateVersion } from "@/utils/reports/types";

import schemas from "./homecareSchemas.json";

// Made-up report for the website. It has the same shape the portal receives from
// fn_report_read (utils/reports/types.ts) and the template schemas of the `homecare` pack, so
// the website draws it with the real report components (ReportDay / ReportMonth) instead of a
// picture that could drift from the product. The schemas live in homecareSchemas.json, copied
// from mobile_form_template_pack_items; the people, medication and values are invented.

/** Last day of the sample, fixed so the server and the browser render the same report. */
export const DEMO_TODAY = "2026-10-26";
const DEMO_FIRST_DAY = addDays(DEMO_TODAY, -29);

export const DEMO_COMPANY = "Cuidados Vida";
export const DEMO_PATIENT = "Elena Rojas";

const TEMPLATE_KEYS = [
  "signos_vitales",
  "medicamentos",
  "alimentacion",
  "ingestas_excretas",
  "actividades",
  "bitacora",
] as const;
type TemplateKey = (typeof TEMPLATE_KEYS)[number];

const VERSION_IDS: Record<TemplateKey, number> = {
  signos_vitales: 1,
  medicamentos: 2,
  alimentacion: 3,
  ingestas_excretas: 4,
  actividades: 5,
  bitacora: 6,
};

const MORNING_SHIFT = { start: "07:00", end: "15:00", author: "Marta Solano" } as const;
const EVENING_SHIFT = { start: "15:00", end: "23:00", author: "Karla Mora" } as const;

// Costa Rica has no daylight saving time, so the offset is constant.
const UTC_OFFSET = "-06:00";

function templates(): Record<string, ReportTemplateVersion> {
  return Object.fromEntries(
    TEMPLATE_KEYS.map((key) => [
      String(VERSION_IDS[key]),
      {
        template_key: key,
        template_name: schemas[key].name,
        version: schemas[key].version,
        // The JSON import widens every literal to string; the content is the stored schema.
        schema: schemas[key].schema as unknown as FormSchema,
      },
    ]),
  );
}

/** Small deterministic generator, so the sample never changes between renders. */
function seeded(seed: number): () => number {
  let state = seed;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function authorAt(time: string): string {
  return Number(time.slice(0, 2)) < 15 ? MORNING_SHIFT.author : EVENING_SHIFT.author;
}

function shiftAt(time: string): { start: string; end: string } {
  const shift = Number(time.slice(0, 2)) < 15 ? MORNING_SHIFT : EVENING_SHIFT;
  return { start: shift.start, end: shift.end };
}

function buildAll(): ReportSubmission[] {
  const rows: ReportSubmission[] = [];
  let nextId = 1;
  const rand = seeded(2610);
  // Value around `center`, up to `spread` either side.
  const around = (center: number, spread: number) => center + (rand() * 2 - 1) * spread;

  const add = (
    key: TemplateKey,
    date: string,
    time: string,
    answers: FormAnswers,
    shift: { start: string; end: string } = shiftAt(time),
    author: string = authorAt(time),
  ) => {
    rows.push({
      id: nextId++,
      template_version_id: VERSION_IDS[key],
      shift_date: date,
      occurred_at: `${date}T${time}:00${UTC_OFFSET}`,
      patient_ids: [1],
      author_name: author,
      shift,
      answers,
      soft_warnings: [],
    });
  };

  for (let d = 0; d < 30; d++) {
    const date = addDays(DEMO_FIRST_DAY, d);
    const isToday = date === DEMO_TODAY;

    // Vital signs: three readings a day. Two values sit outside the usual range, on days that
    // are not the last one, to show how the report flags them.
    const readings = ["07:10", "13:05", "19:20"];
    readings.forEach((time, i) => {
      const highPressure = d === 11 && i === 1;
      const fever = d === 19 && i === 2;
      add("signos_vitales", date, time, {
        measured_at: `${date}T${time}`,
        systolic: highPressure ? 148 : Math.round(around(123, 7)),
        diastolic: highPressure ? 94 : Math.round(around(78, 4)),
        heart_rate: Math.round(around(72, 5)),
        oxygen_saturation: Math.round(around(96.5, 1.2)),
        temperature: fever ? 37.8 : Math.round(around(36.6, 0.25) * 10) / 10,
        respiratory_rate: Math.round(around(17, 1.4)),
        ...(i === 0 ? { glucose: Math.round(around(104, 10)) } : {}),
      });
    });

    // Medication: what the caregiver recorded. One dose is missed on day 7.
    const doses: { time: string; medication: string; dose: string }[] = [
      { time: "08:00", medication: "Losartán", dose: "50 mg" },
      { time: "08:00", medication: "Metformina", dose: "850 mg" },
      { time: "20:00", medication: "Metformina", dose: "850 mg" },
      { time: "20:00", medication: "Atorvastatina", dose: "20 mg" },
    ];
    for (const dose of doses) {
      const missed = d === 7 && dose.time === "20:00" && dose.medication === "Metformina";
      add(
        "medicamentos",
        date,
        dose.time,
        missed
          ? {
              medication: dose.medication,
              administered: false,
              administered_at: dose.time,
              not_given_reason: "No quiso tomarla. Se avisó a la familia.",
            }
          : {
              medication: dose.medication,
              administered: true,
              administered_at: dose.time,
              dose: dose.dose,
              route: "oral",
              instructions: dose.dose === "850 mg" ? "Con la comida" : "",
            },
      );
    }

    // Meals.
    const meals: { meal: string; time: string; portion: number; fluids: number }[] = [
      { meal: "desayuno", time: "08:30", portion: 4, fluids: 200 },
      { meal: "almuerzo", time: "12:30", portion: 3, fluids: 250 },
      { meal: "merienda", time: "15:30", portion: 3, fluids: 150 },
      { meal: "cena", time: "18:45", portion: 2, fluids: 200 },
    ];
    for (const meal of meals) {
      const portion = Math.max(1, Math.min(4, Math.round(around(meal.portion, 1))));
      add("alimentacion", date, meal.time, {
        meal: meal.meal,
        served_at: `${date}T${meal.time}`,
        portion_eaten: portion,
        appetite: portion >= 3 ? "bueno" : portion === 2 ? "regular" : "malo",
        fluids_ml: meal.fluids,
        ...(isToday && meal.meal === "cena" ? { notes: "Prefirió una porción pequeña." } : {}),
      });
    }

    // Intake and output.
    const intake = (time: string, drink: string, volume: number) =>
      add("ingestas_excretas", date, time, {
        occurred_at: `${date}T${time}`,
        category: "ingesta",
        intake_type: "liquid",
        drink,
        drink_accepted: true,
        volume_ml: volume,
      });
    intake("10:00", "water", 250);
    intake("16:30", "tea_coffee", 200);
    intake("21:00", "water", 150);

    const urine = (time: string, amount: string, color: string, volume: number) =>
      add("ingestas_excretas", date, time, {
        occurred_at: `${date}T${time}`,
        category: "excreta",
        excreta_type: "urine",
        urine_amount: amount,
        urine_color: color,
        urine_ml: volume,
      });
    urine("09:00", "medium", "yellow", Math.round(around(300, 60)));
    urine("14:30", "medium", "very_light", Math.round(around(260, 60)));
    urine("20:15", "low", "yellow", Math.round(around(200, 50)));
    if (d % 2 === 0) {
      add("ingestas_excretas", date, "11:20", {
        occurred_at: `${date}T11:20`,
        category: "excreta",
        excreta_type: "stool",
        stool_type: "4",
        stool_amount: "medium",
      });
    }

    // Activities of each shift, with the mood of the person.
    const activityPool = ["bano", "cambio_ropa", "movilizacion", "paseo", "ejercicios", "acompanamiento", "aseo_area"];
    for (const shift of [MORNING_SHIFT, EVENING_SHIFT]) {
      const done = activityPool.filter(() => rand() > 0.4);
      if (!done.includes("acompanamiento")) done.push("acompanamiento");
      add(
        "actividades",
        date,
        shift.end,
        {
          activities: done,
          mood: Math.max(2, Math.min(5, Math.round(around(4, 1.1)))),
          ...(done.includes("paseo") ? { walk_minutes: 15 + Math.round(rand() * 3) * 5 } : {}),
          ...(isToday && shift === MORNING_SHIFT ? { notes: "Mañana tranquila, con buen ánimo." } : {}),
        },
        { start: shift.start, end: shift.end },
        shift.author,
      );
    }

    // Log.
    const notes: { time: string; note: string }[] = isToday
      ? [
          { time: "09:15", note: "Salió al jardín con la cuidadora, unos 20 minutos." },
          { time: "11:40", note: "Descansó después del baño, sin molestias." },
          { time: "17:30", note: "Pidió agua dos veces y se le ofreció." },
        ]
      : [{ time: d % 2 === 0 ? "10:30" : "16:45", note: d % 3 === 0 ? "Recibió la visita de su hija." : "Sin novedades en el turno." }];
    for (const entry of notes) {
      add("bitacora", date, entry.time, { occurred_at: `${date}T${entry.time}`, note: entry.note });
    }
  }
  return rows;
}

const ALL_SUBMISSIONS = buildAll();

/** The report of the sample patient for a range of days, as the portal would receive it. */
export function buildDemoReport(from: string, to: string): ReportPayload {
  return {
    contract_id: 1,
    company_id: 1,
    company: { name: DEMO_COMPANY, logo_url: null },
    patients: [{ id: 1, name: DEMO_PATIENT }],
    from,
    to,
    effective_from: from,
    timezone: "America/Costa_Rica",
    unit_symbols: {
      C: "°C",
      mg_dl: "mg/dL",
      mm_hg: "mmHg",
      pct: "%",
      bpm: "lpm",
      rpm: "rpm",
      ml: "ml",
    },
    templates: templates(),
    submissions: ALL_SUBMISSIONS.filter((s) => s.shift_date >= from && s.shift_date <= to),
  };
}

/** The two reports the portal offers: one day and the last 30 days. */
export function buildDemoReports(): { day: ReportPayload; month: ReportPayload } {
  return {
    day: buildDemoReport(DEMO_TODAY, DEMO_TODAY),
    month: buildDemoReport(addDays(DEMO_TODAY, -29), DEMO_TODAY),
  };
}
